import React from "react";
import { Table } from "semantic-ui-react";

export const mapPerformaApiToPdf = (data) => ({
  date: data?.invoiceDate,
  to: data?.invoiceName,
  companyName: data?.companyName || data?.invoiceName || "",
  customerContact: data?.customerContact || data?.mobile || "",
  mobile: data?.customerContact || data?.mobile || "",
  email: data?.email || data?.emailId || "",
  invoiceNumber: data?.invoiceNumber,
  personName: data?.personName,
  workName: data?.workName,
  additionalInfo: data?.additionalInfo,
  bankDetails: data?.bankDetails,
  rowData: data?.itemList || [],
});

export const PERFORMA_PRINT_PAGE_STYLE = `
  @page {
    size: A4;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
  }
  .performa-pdf {
    padding: 12mm !important;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .performa-pdf thead {
    display: table-header-group !important;
  }
  .performa-pdf .pdf-table-lead-in {
    height: 12mm !important;
  }
  .performa-pdf .pdf-items-table {
    margin-top: -12mm !important;
  }
  .performa-pdf .pdf-continued-page-spacer,
  .performa-pdf .pdf-continued-page-spacer > th,
  .performa-pdf .pdf-continued-page-spacer > td {
    height: 12mm !important;
    min-height: 12mm !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    font-size: 0 !important;
    line-height: 0 !important;
  }
  .performa-pdf .pdf-table-header {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .performa-pdf .pdf-top-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    width: 100% !important;
    gap: 16px !important;
    margin-bottom: 8px !important;
  }
  .performa-pdf .pdf-branding {
    flex: 0 1 40% !important;
  }
  .performa-pdf .pdf-brand-title {
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 1.25 !important;
    margin: 0 !important;
  }
  .performa-pdf .pdf-brand-sub {
    font-weight: 400 !important;
    font-size: 13px !important;
    line-height: 1.4 !important;
    margin-top: 2px !important;
  }
  .performa-pdf .pdf-details-wrap {
    flex: 0 1 54% !important;
    min-width: 280px !important;
  }
  .performa-pdf .pdf-doc-title {
    font-weight: 700 !important;
    font-size: 16px !important;
    text-align: right !important;
    margin: 0 0 14px 0 !important;
  }
  .performa-pdf .pdf-detail-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 24px !important;
    margin-bottom: 6px !important;
    font-size: 13px !important;
    line-height: 1.45 !important;
  }
  .performa-pdf .pdf-detail-label {
    flex: 0 0 auto !important;
    text-align: left !important;
    white-space: nowrap !important;
    font-weight: 400 !important;
  }
  .performa-pdf .pdf-detail-value {
    flex: 1 1 auto !important;
    text-align: right !important;
    font-weight: 600 !important;
    white-space: pre-wrap !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    overflow: visible !important;
    max-height: none !important;
  }
  .performa-pdf .pdf-work-name,
  .performa-pdf .pdf-bank-details,
  .performa-pdf .pdf-bank-details-row {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    white-space: pre-wrap !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    overflow: visible !important;
    max-height: none !important;
  }
`;

export const suppressPerformaPrintChrome = () => {
  const iframe = document.getElementById("printWindow");
  if (iframe && iframe.contentDocument) {
    iframe.contentDocument.title = "";
  }
};

export const EMPTY_BANK_DETAILS = {
  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  iban: "",
  swiftBic: "",
  branch: "",
  bankAddress: "",
  country: "",
  currency: "",
  routingNumber: "",
};

export const BANK_DETAIL_FIELDS = [
  { key: "accountHolderName", label: "Account Holder Name" },
  { key: "bankName", label: "Bank Name" },
  { key: "accountNumber", label: "Account Number" },
  { key: "iban", label: "IBAN" },
  { key: "swiftBic", label: "SWIFT / BIC" },
  { key: "branch", label: "Branch" },
  { key: "bankAddress", label: "Bank Address" },
  { key: "country", label: "Country" },
  { key: "currency", label: "Currency" },
  { key: "routingNumber", label: "Routing Number" },
];

export const parseBankDetails = (raw) => {
  if (!raw) {
    return { ...EMPTY_BANK_DETAILS };
  }
  let parsed = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      return { ...EMPTY_BANK_DETAILS };
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      return { ...EMPTY_BANK_DETAILS };
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ...EMPTY_BANK_DETAILS };
  }
  return {
    accountHolderName: parsed.accountHolderName || "",
    bankName: parsed.bankName || "",
    accountNumber: parsed.accountNumber || "",
    iban: parsed.iban || "",
    swiftBic: parsed.swiftBic || "",
    branch: parsed.branch || "",
    bankAddress: parsed.bankAddress || "",
    country: parsed.country || "",
    currency: parsed.currency || "",
    routingNumber: parsed.routingNumber || "",
  };
};

export const hasBankDetails = (details) =>
  Object.values(details || {}).some(
    (value) => String(value || "").trim() !== ""
  );

const uniqueNonEmpty = (values) => {
  const seen = new Set();
  const result = [];
  (values || []).forEach((value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(trimmed);
  });
  return result;
};

const formatBankDetailsLines = (dataObject) => {
  const fromPayload =
    dataObject?.bankDetails && typeof dataObject.bankDetails === "object"
      ? { ...EMPTY_BANK_DETAILS, ...dataObject.bankDetails }
      : null;
  const details = hasBankDetails(fromPayload)
    ? fromPayload
    : parseBankDetails(dataObject?.additionalInfo);
  if (!hasBankDetails(details)) {
    return [];
  }

  const lines = [];
  const bankName = String(details.bankName || "").trim();
  const location = uniqueNonEmpty([
    details.branch,
    details.country,
    details.bankAddress,
  ]).find(
    (part) => !bankName || bankName.toLowerCase().indexOf(part.toLowerCase()) === -1
  ) || "";
  const nameLine = [bankName, location].filter(Boolean).join(" - ");
  if (nameLine) {
    lines.push(nameLine);
  }

  const accountNumber = String(details.accountNumber || "").trim();
  if (accountNumber) {
    lines.push("ACCOUNT NUMBER");
    lines.push(accountNumber);
  }

  const iban = String(details.iban || "").trim();
  if (iban) {
    lines.push(`IBAN ${iban}`);
  }

  return lines.filter((line) => String(line || "").trim() !== "");
};

const PdfDetailRow = ({ label, value, textColor, alwaysShow }) => {
  const display = value == null ? "" : String(value);
  if (!alwaysShow && display.trim() === "") {
    return null;
  }
  return (
    <div
      className="pdf-detail-row"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 24,
        marginBottom: 6,
        fontSize: 13,
        lineHeight: 1.45,
        color: textColor,
      }}
    >
      <div
        className="pdf-detail-label"
        style={{ flex: "0 0 auto", textAlign: "left", whiteSpace: "nowrap", fontWeight: 400 }}
      >
        {label}
      </div>
      <div
        className="pdf-detail-value"
        style={{
          flex: "1 1 auto",
          textAlign: "right",
          fontWeight: 600,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {display}
      </div>
    </div>
  );
};

const PerformaInvoicePrintView = React.forwardRef(({ dataObject }, ref) => {
  const textColor = "#6e6e6e";
  const headerBg = "#e6e6e6";
  const bankLines = formatBankDetailsLines(dataObject);
  const bankDetailsValue = bankLines.join("\n");
  const companyName =
    dataObject?.companyName || dataObject?.to || dataObject?.invoiceName || "";
  const contactMobile =
    dataObject?.customerContact || dataObject?.mobile || "";
  const email = dataObject?.email || dataObject?.emailId || "";
  const headerStyle = {
    backgroundColor: headerBg,
    color: textColor,
    fontWeight: "bold",
    borderColor: "#d0d0d0",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };
  const cellStyle = {
    padding: "5px",
    color: textColor,
    borderColor: "#d0d0d0",
  };
  const stylesForPrint = `
  ${PERFORMA_PRINT_PAGE_STYLE}
  @media print {
    body {
      margin: 0;
      color: ${textColor};
    }
    .performa-pdf {
      padding: 12mm !important;
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .performa-pdf, .performa-pdf * {
      color: ${textColor} !important;
      border-color: #d0d0d0 !important;
    }
    .performa-pdf .pdf-table-header {
      background-color: ${headerBg} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .performa-pdf .pdf-continued-page-spacer,
    .performa-pdf .pdf-continued-page-spacer > th,
    .performa-pdf .pdf-continued-page-spacer > td {
      border: none !important;
      background: transparent !important;
      background-color: transparent !important;
    }
    table {
      page-break-inside: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group !important;
    }
  }
`;

  return (
    <div
      ref={ref}
      className="performa-pdf"
      style={{
        width: "210mm",
        padding: 30,
        backgroundColor: "#ffffff",
        color: textColor,
        boxSizing: "border-box",
      }}
    >
      <style>{stylesForPrint}</style>
      <div
        className="pdf-top-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
          gap: 16,
          marginBottom: 8,
        }}
      >
        <div className="pdf-branding" style={{ flex: "0 1 40%" }}>
          <div
            className="pdf-brand-title"
            style={{
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.25,
              margin: 0,
              color: textColor,
            }}
          >
            PARMAR GROUP
          </div>
          <div
            className="pdf-brand-sub"
            style={{
              fontWeight: 400,
              fontSize: 13,
              lineHeight: 1.4,
              marginTop: 2,
              color: textColor,
            }}
          >
            SILVER STAR
          </div>
        </div>
        <div className="pdf-details-wrap" style={{ flex: "0 1 54%", minWidth: 280 }}>
          <div
            className="pdf-doc-title"
            style={{
              fontWeight: 700,
              fontSize: 16,
              textAlign: "right",
              margin: "0 0 14px 0",
              color: textColor,
            }}
          >
            ALL ATTRACTIONS
          </div>
          <PdfDetailRow label="Date" value={dataObject?.date} textColor={textColor} />
          <PdfDetailRow
            label="Company Name"
            value={companyName}
            textColor={textColor}
            alwaysShow
          />
          <PdfDetailRow label="Contact Person Name" value={dataObject?.personName} textColor={textColor} />
          <PdfDetailRow
            label="Contact Person Mobile"
            value={contactMobile}
            textColor={textColor}
            alwaysShow
          />
          <PdfDetailRow
            label="Email ID"
            value={email}
            textColor={textColor}
            alwaysShow
          />
          <PdfDetailRow label="Work Name" value={dataObject?.workName} textColor={textColor} />
          <PdfDetailRow label="Bank Details" value={bankDetailsValue} textColor={textColor} />
        </div>
      </div>
      <div className="pdf-table-lead-in" />
      <Table celled structured className="pdf-items-table" style={{ color: textColor }}>
        <Table.Header>
          <Table.Row className="pdf-continued-page-spacer">
            <Table.HeaderCell colSpan={5}>{'\u00a0'}</Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell rowSpan="2" className="pdf-table-header" style={headerStyle}>
              ATTRACTIONS
            </Table.HeaderCell>
            <Table.HeaderCell colSpan="2" textAlign="center" className="pdf-table-header" style={headerStyle}>
              NON PRIME
            </Table.HeaderCell>
            <Table.HeaderCell colSpan="2" textAlign="center" className="pdf-table-header" style={headerStyle}>
              PRIME TIME
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell textAlign="center" className="pdf-table-header" style={headerStyle}>
              ADULT
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center" className="pdf-table-header" style={headerStyle}>
              CHILD
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center" className="pdf-table-header" style={headerStyle}>
              ADULT
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center" className="pdf-table-header" style={headerStyle}>
              CHILD
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(dataObject?.rowData || []).map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell style={cellStyle}>{item?.allAttractions}</Table.Cell>
              <Table.Cell style={cellStyle}>{item?.nonPrimeAdult}</Table.Cell>
              <Table.Cell style={cellStyle}>{item?.nonPrimeChild}</Table.Cell>
              <Table.Cell style={cellStyle}>{item?.primeTimeAdult}</Table.Cell>
              <Table.Cell style={cellStyle}>{item?.primeTimeChild}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <div style={{ textAlign: "center", padding: "20px 10px", color: textColor }}>
        <h4 style={{ color: textColor }}> Thank you for your business</h4>
      </div>
    </div>
  );
});

PerformaInvoicePrintView.displayName = "PerformaInvoicePrintView";

export default PerformaInvoicePrintView;
